import React, { useMemo, useCallback } from 'react'
import { renderContent, sanitizeAnswerHTML } from '../../utils/contentRenderer'
import { createLogger } from '../../utils/logger'

const logger = createLogger('MultipleChoiceQuestion')

// Removed React.memo to allow immediate re-renders when cross-out state changes
const MultipleChoiceQuestion = ({
  question,
  details,
  selectedAnswer,
  onAnswerSelect,
  onCrossOut,
  isAnswerCrossedOut,
  crossedOutAnswers = [],
  readOnly = false,
  showCorrectAnswer = false,
  correctAnswer = null
}) => {
  // Check if details and answerOptions exist
  if (!details) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('❌ MultipleChoiceQuestion: No details provided')
    }
    return (
      <div className="text-center py-4">
        <p className="text-red-500">Error: Question details not found</p>
      </div>
    )
  }

  if (!details.answerOptions || !Array.isArray(details.answerOptions)) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('❌ MultipleChoiceQuestion: No answer options found', details.answerOptions)
    }
    return (
      <div className="text-center py-4">
        <p className="text-red-500">Error: Answer options not found</p>
      </div>
    )
  }

  // Memoize the question ID to avoid recalculation
  const questionId = useMemo(() => question.id, [question.id])

  // Normalize correctAnswer for comparison (handle array format like ["A"])
  const normalizedCorrectAnswer = useMemo(() => {
    if (!correctAnswer) return null
    if (Array.isArray(correctAnswer)) {
      return correctAnswer.length > 0 ? String(correctAnswer[0]).toUpperCase() : null
    }
    return String(correctAnswer).toUpperCase()
  }, [correctAnswer])

  // Memoize option components to prevent unnecessary re-renders
  const optionComponents = useMemo(() => {
    return details.answerOptions.map((option, index) => {
      const optionLabel = String.fromCharCode(65 + index)
      const isSelected = selectedAnswer === optionLabel
      // ✅ Fix: isAnswerCrossedOut only takes one parameter (answer)
      const isCrossedOut = isAnswerCrossedOut ? isAnswerCrossedOut(optionLabel) : false

      // In review mode, check if this is the correct answer
      const isCorrectOption = showCorrectAnswer && normalizedCorrectAnswer === optionLabel
      const isWrongSelection = showCorrectAnswer && isSelected && !isCorrectOption

      // Removed inline styles - using Tailwind classes for dark mode support

      const handleClick = () => {
        // Don't allow clicks in read-only mode
        if (readOnly) return
        if (!isCrossedOut) {
          onAnswerSelect(optionLabel)
        }
      }

      const handleCrossOutClick = (e) => {
        e.stopPropagation()
        // Don't allow cross-out in read-only mode
        if (readOnly) return
        onCrossOut(optionLabel)
      }

      // Determine styling based on state
      let optionClassName = 'answer-option rounded-lg p-3 sm:p-4 transition-all duration-200 '

      if (readOnly) {
        // Read-only mode styling
        optionClassName += 'cursor-default '
        if (isCorrectOption) {
          // Correct answer - green
          optionClassName += 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20'
        } else if (isWrongSelection) {
          // User selected wrong answer - red
          optionClassName += 'border-2 border-red-500 bg-red-50 dark:bg-red-900/20'
        } else if (isSelected) {
          // User's correct selection
          optionClassName += 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20'
        } else {
          // Unselected option in read-only mode
          optionClassName +=
            'border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 opacity-70'
        }
      } else {
        // Edit mode styling (original behavior)
        optionClassName += 'cursor-pointer '
        if (isSelected) {
          optionClassName +=
            'border-3 border-primary-600 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md'
        } else if (isCrossedOut) {
          optionClassName +=
            'border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 opacity-60'
        } else {
          optionClassName +=
            'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-sm'
        }
      }

      if (isCrossedOut && !readOnly) {
        optionClassName += ' crossed-out'
      }

      // Determine badge circle styling
      let badgeClassName =
        'w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0 transition-colors '

      if (readOnly && isCorrectOption) {
        badgeClassName += 'bg-green-600 text-white border-2 border-green-700'
      } else if (readOnly && isWrongSelection) {
        badgeClassName += 'bg-red-600 text-white border-2 border-red-700'
      } else if (isSelected) {
        badgeClassName +=
          'bg-primary-600 dark:bg-primary-500 text-white border-2 border-primary-700 dark:border-primary-600 shadow-sm'
      } else if (isCrossedOut) {
        badgeClassName += 'bg-red-200 dark:bg-red-900/30 text-red-700 dark:text-red-400'
      } else {
        badgeClassName += 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
      }

      return (
        <div key={option.id} className={optionClassName} onClick={handleClick}>
          <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <div className={badgeClassName}>{optionLabel}</div>
            <div className="flex-1 min-w-0">
              <div
                className={`question-content text-sm sm:text-base ${isCrossedOut && !readOnly ? 'crossed-out-text' : ''}`}
              >
                {renderContent(option.content, question?.contentType || question?.content_type)}
              </div>
            </div>
            {/* Show correct/wrong indicator in review mode */}
            {readOnly && showCorrectAnswer && (
              <div className="flex-shrink-0 ml-2">
                {isCorrectOption && (
                  <span className="text-green-600 font-medium text-sm">Correct</span>
                )}
                {isWrongSelection && (
                  <span className="text-red-600 font-medium text-sm">Your answer</span>
                )}
              </div>
            )}
          </div>
          {/* Hide cross-out button in read-only mode */}
          {onCrossOut && !readOnly && (
            <button
              onClick={handleCrossOutClick}
              className={`p-1 sm:p-1.5 rounded transition-colors flex-shrink-0 ${
                isCrossedOut
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={isCrossedOut ? 'Uncross this answer' : 'Cross out this answer'}
            >
              {isCrossedOut ? '✓' : '✕'}
            </button>
          )}
        </div>
      )
    })
  }, [
    details.answerOptions,
    selectedAnswer,
    questionId,
    crossedOutAnswers,
    onAnswerSelect,
    onCrossOut,
    readOnly,
    showCorrectAnswer,
    normalizedCorrectAnswer
  ])

  return (
    <div className="space-y-2 sm:space-y-3">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
        Answer Choices
      </h3>
      {optionComponents}
    </div>
  )
}

export default MultipleChoiceQuestion
